import { redirect } from "@tanstack/react-router";
import { can } from "@/lib/staff-roles";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { CheckCircle2, Clock, XCircle, RefreshCw, MailCheck } from "lucide-react";
import { fetchEmailStatus } from "@/lib/email-status.functions";

export const Route = createFileRoute("/_authenticated/admin/email-status")({
  beforeLoad: ({ context }) => {
    if (!can(context.staffRole, "viewMoney")) {
      throw redirect({ to: "/admin/calendar" });
    }
  },
  head: () => ({
    meta: [
      { title: "Email Status — Coffman Cove Lodging" },
      {
        name: "description",
        content:
          "Internal status board confirming DNS and sending verification for the lodge notification email domain.",
      },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdminEmailStatus,
});

type Status = Awaited<ReturnType<typeof fetchEmailStatus>>;

function StateIcon({ ok }: { ok: boolean | "pending" }) {
  if (ok === true) return <CheckCircle2 className="text-emerald-500" size={20} aria-hidden />;
  if (ok === "pending") return <Clock className="text-amber-500" size={20} aria-hidden />;
  return <XCircle className="text-destructive" size={20} aria-hidden />;
}

function AdminEmailStatus() {
  const run = useServerFn(fetchEmailStatus);
  const [data, setData] = useState<Status | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData((await run({})) as Status);
    } catch {
      setError("Could not load email status. Make sure you're signed in as an admin.");
    } finally {
      setLoading(false);
    }
  }, [run]);

  useEffect(() => {
    void load();
  }, [load]);

  const verified = data?.sending === "verified" && data.dnsOk;

  return (
    <div className="mx-auto max-w-3xl px-4 py-12">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-[var(--color-ember)]">
            <MailCheck size={18} aria-hidden />
            <span className="text-xs font-semibold uppercase tracking-[0.25em]">Staff tools</span>
          </div>
          <h1 className="font-display text-3xl mt-2">Email verification status</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Live check of {data?.senderDomain ?? "notify.coffmancovelodging.com"}.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => void load()}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm hover:border-[var(--color-ember)] disabled:opacity-60"
          >
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} aria-hidden />
            Re-check
          </button>
          <Link
            to="/admin/inbox"
            className="inline-flex items-center rounded-md border border-border px-3 py-2 text-sm hover:border-[var(--color-ember)]"
          >
            Inbox
          </Link>
        </div>
      </header>

      {error && (
        <p className="mt-8 rounded-md border border-destructive/40 bg-destructive/10 p-4 text-sm">{error}</p>
      )}

      {data && (
        <>
          <section
            className={`mt-8 rounded-xl border p-6 ${
              verified ? "border-emerald-500/40 bg-emerald-500/5" : "border-amber-500/40 bg-amber-500/5"
            }`}
          >
            <div className="flex items-center gap-3">
              <StateIcon ok={verified ? true : "pending"} />
              <h2 className="font-display text-xl">
                {verified ? "Verification complete — emails are sending" : "Verification still pending"}
              </h2>
            </div>
            <p className="mt-2 text-sm text-muted-foreground">{data.sendingDetail}</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Checked {new Date(data.checkedAt).toLocaleString("en-US")}
            </p>
          </section>

          <section className="mt-8">
            <h3 className="text-xs font-semibold uppercase tracking-[0.25em] text-muted-foreground">
              DNS records
            </h3>
            <ul className="mt-3 space-y-3">
              {data.dns.map((d) => (
                <li key={d.host + d.type} className="rounded-lg border border-border p-4">
                  <div className="flex items-center gap-3">
                    <StateIcon ok={d.ok} />
                    <div>
                      <p className="text-sm font-medium">{d.label}</p>
                      <p className="text-xs text-muted-foreground">
                        {d.type} · {d.host}
                      </p>
                    </div>
                  </div>
                  <p className="mt-2 break-all text-xs text-muted-foreground">
                    {d.found.length ? d.found.join(", ") : "Not visible in public DNS yet"}
                  </p>
                </li>
              ))}
            </ul>
          </section>

          {data.recentEvents.length > 0 && (
            <section className="mt-8">
              <h3 className="text-xs font-semibold uppercase tracking-[0.25em] text-muted-foreground">
                Recent delivery events
              </h3>
              <ul className="mt-3 divide-y divide-border rounded-lg border border-border">
                {data.recentEvents.map((e, i) => (
                  <li key={i} className="flex flex-wrap items-center justify-between gap-2 p-3 text-sm">
                    <span className="font-medium">{e.eventType}</span>
                    <span className="text-muted-foreground">{e.recipient}</span>
                    <span className="text-xs text-muted-foreground">
                      {new Date(e.timestamp).toLocaleString("en-US")}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}
    </div>
  );
}
